// v1.9.22 — Permissions page. Admin picks a role from the left, toggles
// section + action grants on the right, sees a live sidebar preview.
import React, { useEffect, useMemo, useState } from 'react';
import { FiSave, FiRefreshCw, FiLock, FiAlertTriangle } from 'react-icons/fi';
import { getPermissions, updateRolePermissions } from '../../services/api';

const ROLE_LABELS = {
  admin: 'Admin', pastor: 'Pastor', finance: 'Finance', cashier: 'Cashier',
  secretary: 'Secretary', cell_leader: 'Cell Leader',
};

const SECTION_META = {
  members:    { icon: '👥', title: 'Members',    path: '/members',    kids: ['All members list', 'Add member', 'Membership status'] },
  attendance: { icon: '✓',  title: 'Attendance', path: '/attendance', kids: ['Record attendance', 'History', 'Absentee report'] },
  spiritual:  { icon: '♡',  title: 'Spiritual Care', path: '/spiritual', kids: ['Spiritual fathers', 'Assign members', 'Follow-ups'] },
  finance:    { icon: '$',  title: 'Finance',    path: '/finance',    kids: ['Cash Receipts', 'Pending Approvals', 'Payment Vouchers', 'Accounts Payable', 'Cash Book'] },
  stock:      { icon: '📦', title: 'Stock',      path: '/stock',      kids: ['GRN', 'SIV', 'Fixed Assets'] },
  reports:    { icon: '📊', title: 'Reports',    path: '/reports',    kids: ['Financial Summary', 'Subscription Status', 'Spiritual Father', 'Membership'] },
  settings:   { icon: '⚙',  title: 'Settings',   path: '/settings',   kids: ['Church Profile', 'Users & Roles', 'Permissions', 'Funds', 'Opening Balance', 'Departments'] },
  info:       { icon: 'ℹ',  title: 'Information', path: '/info',      kids: ['Information Hub'] },
};

const ACTION_META = {
  'finance.cr.approve':          { title: 'Approve Cash Receipts', section: 'finance', desc: 'Clear items from Pending Approvals' },
  'finance.cr.delete.request':   { title: 'Request CR deletion',   section: 'finance', desc: 'Start a delete request (needs opposite role to approve)' },
  'finance.cr.delete.approve':   { title: 'Approve CR deletion',   section: 'finance', desc: 'Finalise or reject a deletion request' },
  'finance.pv.approve_finance':  { title: 'Approve PV (Finance step)', section: 'finance', desc: 'First-level PV approval' },
  'finance.pv.approve_admin':    { title: 'Approve PV (Admin step)',   section: 'finance', desc: 'Final PV approval' },
};

const Permissions = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [selectedRole, setSelectedRole] = useState('cashier');
  const [draft, setDraft] = useState(new Set());
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getPermissions();
      setData(r.data);
      const cur = r.data.grants[selectedRole] || [];
      setDraft(new Set(cur));
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Could not load permissions' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!data) return;
    setDraft(new Set(data.grants[selectedRole] || []));
    setMsg(null);
  }, [selectedRole]); // eslint-disable-line

  const isAdmin = selectedRole === 'admin';
  const original = useMemo(() => new Set((data?.grants || {})[selectedRole] || []), [data, selectedRole]);
  const dirty = useMemo(() => {
    if (draft.size !== original.size) return true;
    for (const k of draft) if (!original.has(k)) return true;
    return false;
  }, [draft, original]);

  const toggle = (key) => {
    if (isAdmin) return;
    const next = new Set(draft);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDraft(next);
  };
  const toggleSectionAll = (section) => {
    if (isAdmin) return;
    const relatedActions = (data.actions || []).filter(a => ACTION_META[a]?.section === section);
    const has = draft.has(section);
    const next = new Set(draft);
    if (has) {
      next.delete(section);
      relatedActions.forEach(a => next.delete(a));
    } else {
      next.add(section);
    }
    setDraft(next);
  };

  const save = async () => {
    if (isAdmin || !dirty) return;
    setSaving(true);
    try {
      await updateRolePermissions(selectedRole, Array.from(draft));
      setMsg({ type: 'ok', text: `Saved. ${ROLE_LABELS[selectedRole]} now has ${draft.size} action(s).` });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: e.response?.data?.error || 'Save failed' });
    } finally { setSaving(false); }
  };

  const discard = () => setDraft(new Set(original));

  if (loading || !data) return <div style={{ padding: 24, color: 'var(--muted, #6b6b7d)' }}>Loading…</div>;

  const sections = data.sections || [];
  const actions  = data.actions  || [];
  const grants   = data.grants   || {};
  const userCounts = data.userCounts || {};

  const previewGroups = sections.filter(s => draft.has(s));

  return (
    <div className="page-content" style={{ paddingBottom: 140 }}>
    <div style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, borderBottom: '1px solid #e4e4ef', paddingBottom: 16, marginBottom: 22, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6b6b7d', fontWeight: 700, marginBottom: 4 }}>Settings · Users &amp; Roles</div>
          <h1 style={{ fontFamily: 'Georgia, serif', margin: 0, color: '#1e1b4b', fontSize: 30, fontWeight: 700 }}>Permissions</h1>
          <p style={{ margin: '6px 0 0', color: '#6b6b7d', maxWidth: '62ch' }}>Pick a role, tick the sections and actions it should have. Changes apply next time users on that role log in.</p>
        </div>
        <button className="btn" onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: 22, alignItems: 'start' }}>
        <div>
          {/* Role picker */}
          <section className="card" style={{ background: '#fff', border: '1px solid #e4e4ef', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #e4e4ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2d2a5e' }}>Roles</strong>
              <span style={{ color: '#6b6b7d', fontSize: 12 }}>Click a role to edit its access</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, padding: '14px 16px' }}>
              {data.roles.map(r => {
                const isSel = r === selectedRole;
                const locked = r === 'admin';
                return (
                  <button key={r} onClick={() => setSelectedRole(r)}
                    style={{
                      position: 'relative', textAlign: 'left', padding: '12px 12px 10px', cursor: 'pointer',
                      background: isSel ? '#eef2ff' : '#fff',
                      border: `1.5px solid ${isSel ? '#4f46e5' : '#e4e4ef'}`,
                      borderRadius: 8, opacity: locked ? 0.9 : 1,
                    }}>
                    {locked && <FiLock style={{ position: 'absolute', top: 8, right: 8, color: '#a1a1aa' }} />}
                    <div style={{ fontWeight: 700, color: isSel ? '#1e1b4b' : '#1e1b4b', fontSize: 14 }}>{ROLE_LABELS[r]}</div>
                    <div style={{ fontFamily: 'Courier New, monospace', fontSize: 11, color: '#6b6b7d', marginTop: 2 }}>{r}</div>
                    <div style={{ marginTop: 8, fontSize: 11, color: '#6b6b7d' }}>
                      <b style={{ color: '#2d2a5e', fontWeight: 700 }}>{(grants[r] || []).length}</b> action(s)
                      {userCounts[r] ? ` · ${userCounts[r]} user${userCounts[r] !== 1 ? 's' : ''}` : ''}
                    </div>
                  </button>
                );
              })}
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 18px 14px', padding: '12px 14px', background: '#fef3c7', border: '1px solid rgba(180,83,9,0.2)', borderRadius: 8, color: '#b45309', fontSize: 12.5 }}>
                <FiAlertTriangle style={{ flexShrink: 0, marginTop: 2 }} />
                <div><b>Admin is locked.</b> It always carries every permission so you can't lock yourself out of Settings.</div>
              </div>
            )}
          </section>

          {/* Sections + actions */}
          <section className="card" style={{ marginTop: 18, background: '#fff', border: '1px solid #e4e4ef', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #e4e4ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2d2a5e' }}>
                Editing · <span style={{ fontFamily: 'Courier New, monospace', textTransform: 'none', letterSpacing: 0, color: '#4f46e5' }}>{selectedRole}</span>
              </strong>
              <span style={{ color: '#6b6b7d', fontSize: 12 }}>{isAdmin ? 'read-only' : `${draft.size} action(s) granted`}</span>
            </div>

            {sections.map(section => {
              const meta = SECTION_META[section] || { icon: '·', title: section, path: `/${section}`, kids: [] };
              const on = draft.has(section);
              const disabled = isAdmin;
              const relatedActions = actions.filter(a => ACTION_META[a]?.section === section);
              return (
                <div key={section} style={{ padding: '14px 18px', borderTop: '1px solid #e4e4ef' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#eef2ff', color: '#4f46e5' }}>{meta.icon}</span>
                      <strong style={{ fontSize: 14.5, color: '#1e1b4b' }}>{meta.title}</strong>
                      <span style={{ fontFamily: 'Courier New, monospace', fontSize: 11, color: '#6b6b7d' }}>{meta.path}</span>
                    </div>
                    <button onClick={() => toggleSectionAll(section)} disabled={disabled}
                      style={{ fontSize: 11.5, color: '#4f46e5', border: 'none', background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                      {on ? '— Remove access' : '+ Grant access'}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                    <PermChip on={on} disabled={disabled} title={`See ${meta.title}`} sub="section access" onClick={() => toggle(section)} />
                    {relatedActions.map(a => {
                      const am = ACTION_META[a] || { title: a, desc: '' };
                      return <PermChip key={a} on={draft.has(a)} disabled={disabled || !on}
                        title={am.title} sub={am.desc || a} onClick={() => toggle(a)} />;
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        </div>

        {/* Live preview */}
        <div style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <section className="card" style={{ background: '#fff', border: '1px solid #e4e4ef', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #e4e4ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2d2a5e' }}>Preview · {ROLE_LABELS[selectedRole]}'s sidebar</strong>
              <span style={{ color: '#6b6b7d', fontSize: 12 }}>Live</span>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ border: '1px solid #e4e4ef', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid #e4e4ef', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontWeight: 700 }}>✝</div>
                  <div>
                    <b style={{ fontSize: 12.5, color: '#1e1b4b' }}>Church CMS</b>
                    <div style={{ fontSize: 10.5, color: '#6b6b7d' }}>Management</div>
                  </div>
                </div>
                <div style={{ padding: '8px 6px 10px' }}>
                  <PreviewItem name="Dashboard" active />
                  {previewGroups.map(g => {
                    const meta = SECTION_META[g] || { title: g, kids: [] };
                    return (
                      <div key={g}>
                        <PreviewItem name={meta.title} />
                        {meta.kids.map(k => <PreviewItem key={k} name={k} indent />)}
                      </div>
                    );
                  })}
                  {previewGroups.length === 0 && (
                    <div style={{ padding: '12px 10px', color: '#b45309', fontSize: 12, background: '#fef3c7', border: '1px dashed #b45309', borderRadius: 6, margin: '6px 4px' }}>
                      This role has no pages beyond Dashboard.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="card" style={{ padding: '14px 18px', background: '#fff', border: '1px solid #e4e4ef', borderRadius: 10 }}>
            <KV k="Actions granted" v={<span style={{ fontFamily: 'Courier New, monospace' }}>{draft.size} / {(data.sections?.length || 0) + (data.actions?.length || 0)}</span>} />
            <KV k="Sidebar groups visible" v={<span style={{ fontFamily: 'Courier New, monospace' }}>{previewGroups.length}</span>} />
            <KV k="Users on this role" v={<span>{userCounts[selectedRole] || 0}</span>} />
            <KV k="Can approve CR?" v={<span style={{ color: draft.has('finance.cr.approve') ? '#166534' : '#6b6b7d', fontWeight: 700 }}>{draft.has('finance.cr.approve') ? 'YES' : 'no'}</span>} last />
          </section>
        </div>
      </div>

      {/* Savebar */}
      {!isAdmin && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: '#fff', borderTop: '1px solid #e4e4ef', boxShadow: '0 -8px 24px rgba(15,15,40,0.06)', zIndex: 10 }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ color: dirty ? '#1e1b4b' : '#6b6b7d', fontSize: 13 }}>
              {msg ? (
                <b style={{ color: msg.type === 'ok' ? '#166534' : '#b91c1c' }}>{msg.text}</b>
              ) : dirty ? (
                <><b>{Math.abs(draft.size - original.size)} pending change{Math.abs(draft.size - original.size) !== 1 ? 's' : ''}</b> to <b>{ROLE_LABELS[selectedRole]}</b></>
              ) : (
                <>No unsaved changes for {ROLE_LABELS[selectedRole]}.</>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" onClick={discard} disabled={!dirty || saving}>Discard</button>
              <button className="btn btn-primary" onClick={save} disabled={!dirty || saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FiSave /> {saving ? 'Saving…' : 'Save permissions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

const PermChip = ({ on, disabled, title, sub, onClick }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', textAlign: 'left',
      border: `1px solid ${on ? '#4f46e5' : '#e4e4ef'}`, borderRadius: 8,
      background: on ? '#eef2ff' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, transition: 'border-color .15s, background .15s',
    }}>
    <span style={{
      flexShrink: 0, width: 16, height: 16, border: `1.5px solid ${on ? '#4f46e5' : '#9a9aa8'}`, borderRadius: 4,
      background: on ? '#4f46e5' : '#fff', marginTop: 1, position: 'relative',
    }}>
      {on && <span style={{ position: 'absolute', left: 4, top: 1, width: 5, height: 9, border: 'solid #fff', borderWidth: '0 2px 2px 0', transform: 'rotate(45deg)' }} />}
    </span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontWeight: 600, color: on ? '#1e1b4b' : '#1e1b4b', fontSize: 12.5 }}>{title}</span>
      <span style={{ fontSize: 10.5, color: '#6b6b7d', letterSpacing: '0.03em', textTransform: 'uppercase', fontWeight: 600 }}>{sub}</span>
    </span>
  </button>
);

const PreviewItem = ({ name, indent, active }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '7px 10px', borderRadius: 6, fontSize: 12.5,
    marginLeft: indent ? 14 : 0,
    color: active ? '#1e1b4b' : '#2d2a5e',
    background: active ? '#eef2ff' : 'transparent',
    fontWeight: active ? 700 : 500,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: 3, background: active ? '#4f46e5' : '#c7d2fe' }} />
    {name}
  </div>
);

const KV = ({ k, v, last }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: 12.5, padding: '6px 0', borderBottom: last ? 'none' : '1px dotted #e4e4ef' }}>
    <span style={{ color: '#6b6b7d' }}>{k}</span>
    <span style={{ color: '#1e1b4b', fontWeight: 600, textAlign: 'right' }}>{v}</span>
  </div>
);

export default Permissions;
